/**
 * WidgetRenderer - Dynamic widget component renderer
 *
 * Maps widget type to appropriate component and passes data.
 * Supports both registry widgets (data-driven) and portal widgets (config-driven).
 */

import React from "react";
import PropTypes from "prop-types";
import { getWidgetById, WIDGET_TYPES } from "../config/widgetRegistry";
import StatWidget from "./StatWidget";
import ChartWidget from "./ChartWidget";
import TableWidget from "./TableWidget";
import GaugeWidget from "./GaugeWidget";
import ActivityFeedWidget from "./ActivityFeedWidget";
import TextWidget from "./TextWidget";

function WidgetRenderer({
  widgetId,
  isExpanded = false,
  portalWidgetConfig = null,
  language = "en",
  userEditable = false,
  onContentChange,
}) {
  // Portal text widgets — these are defined inline in portal page config, not in the registry
  if (portalWidgetConfig && portalWidgetConfig.type === "text") {
    return (
      <TextWidget
        content={portalWidgetConfig.config?.content || ""}
        variant={portalWidgetConfig.config?.variant || "body1"}
        align={portalWidgetConfig.config?.align || "left"}
        color={portalWidgetConfig.config?.color}
        sx={portalWidgetConfig.config?.sx || {}}
        userEditable={userEditable}
        onContentChange={onContentChange}
        language={language}
      />
    );
  }

  // Registry-based widgets
  const effectiveId = portalWidgetConfig?.registryId || widgetId;
  const widget = getWidgetById(effectiveId);

  if (!widget) {
    return null;
  }

  const tableMaxRows = isExpanded ? 50 : 10;

  switch (widget.type) {
    case WIDGET_TYPES.STAT_CARD:
    case WIDGET_TYPES.KPI:
    case WIDGET_TYPES.SPARKLINE:
      return <StatWidget widgetId={effectiveId} isExpanded={isExpanded} />;

    case WIDGET_TYPES.LINE_CHART:
    case WIDGET_TYPES.AREA_CHART:
    case WIDGET_TYPES.BAR_CHART:
    case WIDGET_TYPES.PIE_CHART:
    case WIDGET_TYPES.DONUT:
    case WIDGET_TYPES.HEATMAP:
      return <ChartWidget widgetId={effectiveId} isExpanded={isExpanded} />;

    case WIDGET_TYPES.TABLE:
    case WIDGET_TYPES.LEADERBOARD:
      return <TableWidget widgetId={effectiveId} maxRows={tableMaxRows} isExpanded={isExpanded} />;

    case WIDGET_TYPES.GAUGE:
      return <GaugeWidget widgetId={effectiveId} isExpanded={isExpanded} />;

    case WIDGET_TYPES.TEXT:
      return (
        <TextWidget
          content={widget.config?.content || widget.title || ""}
          variant={widget.config?.variant || "body1"}
          align={widget.config?.align || "left"}
          userEditable={userEditable}
          onContentChange={onContentChange}
          language={language}
        />
      );

    default:
      return <StatWidget widgetId={effectiveId} isExpanded={isExpanded} />;
  }
}

WidgetRenderer.propTypes = {
  widgetId: PropTypes.string.isRequired,
  isExpanded: PropTypes.bool,
  portalWidgetConfig: PropTypes.object,
  language: PropTypes.string,
  userEditable: PropTypes.bool,
  onContentChange: PropTypes.func,
};

export default WidgetRenderer;
